import { useEffect, useMemo, useState } from 'react';
import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import { fetchEasykitInstallers, moveEasykitTask, type EkInstaller } from '@/api/easykit';
import type { OdooTask } from '@/types/odoo';

interface EasykitScheduleOtherModalProps {
  task: OdooTask;
  onClose: () => void;
}

/**
 * Modal that lets Hendrika (projectleider_easykit) schedule an EK task on ANY
 * active Installateur res.partner — including TD installers / onderaannemers
 * not currently visible in the EK grid (Slice 3).
 *
 * Submit path bypasses moveTaskToCell: client-side groupByDriverAndDay only
 * places tasks on drivers in store.driverIds, so a not-yet-known installer
 * would flash into Afhalingen between optimistic update and silentRefresh.
 * Instead we call moveEasykitTask directly + silentRefresh; the server's
 * dynamicRowInstallerIds logic promotes the new driver to a row.
 */
export function EasykitScheduleOtherModal({ task, onClose }: EasykitScheduleOtherModalProps) {
  const silentRefresh = useEasykitPlanningStore((s) => s.silentRefresh);
  const [installers, setInstallers] = useState<EkInstaller[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInstallerId, setSelectedInstallerId] = useState<number | null>(null);
  const initialDate = typeof task.date_deadline === 'string' ? task.date_deadline.slice(0, 10) : '';
  const [targetDate, setTargetDate] = useState(initialDate);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load installers once on mount.
  useEffect(() => {
    let alive = true;
    fetchEasykitInstallers()
      .then((data) => { if (alive) setInstallers(data); })
      .catch(() => { if (alive) setLoadError('Installateurs ophalen mislukt'); });
    return () => { alive = false; };
  }, []);

  // Escape closes the modal; stopPropagation so the global Escape handler
  // in EasykitPlanningView doesn't ALSO clear selections.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true); // capture phase
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // Split into two groups; filter by search query.
  const { ekDrivers, otherInstallers } = useMemo(() => {
    const list = installers ?? [];
    const q = searchQuery.trim().toLowerCase();
    const matches = (i: EkInstaller) =>
      !q || i.name.toLowerCase().includes(q) || (i.companyName ?? '').toLowerCase().includes(q);
    return {
      ekDrivers: list.filter((i) => i.isBaseEasykitDriver && matches(i)),
      otherInstallers: list.filter((i) => !i.isBaseEasykitDriver && matches(i))
        .sort((a, b) => a.name.localeCompare(b.name, 'nl-BE')),
    };
  }, [installers, searchQuery]);

  async function handleSubmit() {
    if (selectedInstallerId === null || !targetDate || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await moveEasykitTask(task.id, targetDate, selectedInstallerId);
      // Server has applied the move; refetch so driverIds picks up the new row.
      await silentRefresh();
      useEasykitPlanningStore.setState({ savedToast: 'Opgeslagen' });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Inplannen mislukt';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ek-schedule-other-title"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 id="ek-schedule-other-title" className="text-base font-bold text-td-navy">
            Plan op iemand anders
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <span className="text-lg text-td-navy" aria-hidden="true">&times;</span>
          </button>
        </div>

        {/* Inline error */}
        {(submitError || loadError) && (
          <div className="mx-5 mt-3 px-3 py-2 rounded bg-red-50 border border-red-200 text-xs text-red-700" role="alert">
            {submitError ?? loadError}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          <p className="text-xs text-td-muted">
            Taak: <span className="font-semibold text-td-navy">{task.name}</span>
          </p>

          <div>
            <label htmlFor="ek-schedule-date" className="block text-xs font-semibold text-td-navy mb-1">Datum</label>
            <input
              id="ek-schedule-date"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue/30 focus:border-td-blue"
            />
          </div>

          <div>
            <label htmlFor="ek-schedule-search" className="block text-xs font-semibold text-td-navy mb-1">Installateur</label>
            <input
              id="ek-schedule-search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Zoek op naam…"
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue/30 focus:border-td-blue"
            />
          </div>

          {installers === null && !loadError && (
            <p className="text-xs text-td-muted text-center py-4">Laden…</p>
          )}

          {installers !== null && (
            <div className="space-y-3">
              {ekDrivers.length > 0 && (
                <InstallerSection title="EK chauffeurs" items={ekDrivers} selectedId={selectedInstallerId} onSelect={setSelectedInstallerId} />
              )}
              {otherInstallers.length > 0 && (
                <InstallerSection title="Andere installateurs" items={otherInstallers} selectedId={selectedInstallerId} onSelect={setSelectedInstallerId} />
              )}
              {ekDrivers.length === 0 && otherInstallers.length === 0 && (
                <p className="text-xs text-td-muted text-center py-4">Geen installateurs gevonden</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-xs font-semibold text-td-navy border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || selectedInstallerId === null || !targetDate}
            className="px-3 py-1.5 text-xs font-semibold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 disabled:opacity-50 transition-colors flex items-center gap-1.5"
          >
            {submitting && <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" aria-hidden="true" />}
            {submitting ? 'Bezig…' : 'Inplannen'}
          </button>
        </div>
      </div>
    </div>
  );
}

function InstallerSection({
  title, items, selectedId, onSelect,
}: {
  title: string;
  items: EkInstaller[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  return (
    <div>
      <p className="text-[10px] font-bold text-td-muted uppercase tracking-wider mb-1">{title}</p>
      <ul className="border border-gray-200 rounded-lg overflow-hidden">
        {items.map((i) => (
          <li key={i.id}>
            <button
              type="button"
              onClick={() => onSelect(i.id)}
              className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between border-b border-gray-100 last:border-b-0 transition-colors ${
                selectedId === i.id ? 'bg-td-blue/10 text-td-blue font-semibold' : 'hover:bg-gray-50 text-td-navy'
              }`}
            >
              <span className="truncate">{i.name}</span>
              {i.companyName && <span className="text-td-muted ml-2 shrink-0">{i.companyName}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
